/**
 * Offline soft-limit paths — plan/14 + plan/28.
 * allow → self-confirm; request → on-device manager PIN if cached.
 */

export type OfflineSoftLimitPath =
  | "self_confirm"
  | "need_approver_pin"
  | "blocked";

/** Decide how to clear a soft limit while offline. */
export function offlineSoftLimitPath(input: {
  /** Cashier may self-allow this permission (override.allow). */
  canSelfAllow: boolean;
  /** At least one cached user can approve this permission. */
  hasCachedApprover: boolean;
}): OfflineSoftLimitPath {
  if (input.canSelfAllow) return "self_confirm";
  if (input.hasCachedApprover) return "need_approver_pin";
  return "blocked";
}

export function offlineSoftLimitBlockedMessage(permissionKey: string): string {
  return `Need a manager PIN cached on this register for ${permissionKey} — go online once so a manager can approve on-device`;
}

export type OfflinePinApprovePayload = {
  id: string;
  permissionKey: string;
  approverUserId: string;
  summary?: string;
};

/** Keys a user may approve offline when they have can_approve (or *). */
export function deriveCanApproveKeys(permissionKeys: string[]): string[] {
  if (permissionKeys.includes("*")) return ["*"];
  if (!permissionKeys.includes("can_approve")) return [];
  return permissionKeys;
}

/** True when a cached user may approve this soft-limit key. */
export function cachedUserCanApprove(
  canApproveKeys: string[] | null | undefined,
  permissionKey: string,
): boolean {
  if (!canApproveKeys || canApproveKeys.length === 0) return false;
  if (canApproveKeys.includes("*")) return true;
  if (canApproveKeys.includes("can_approve") && canApproveKeys.includes(permissionKey)) {
    return true;
  }
  // Cached list may be only the keys they can approve (with can_approve implied)
  return canApproveKeys.includes(permissionKey);
}

export function cachedUserCanSelfAllow(
  permissionKeys: string[] | null | undefined,
  permissionKey: string,
): boolean {
  if (!permissionKeys || permissionKeys.length === 0) return false;
  if (permissionKeys.includes("*")) return true;
  return permissionKeys.includes(permissionKey);
}
